import { readCookie } from 'utils/Cookies';
import { Environment } from 'utils/Environment';
import { isAnalyticsEnabled, updateServicesWithAnalyticsId } from './analytics';

export const ANALYTICS_COOKIE_NAME = 'clAnalyticsId';

export const HEAP_PROD_ID = '1285302910';
export const HEAP_TEST_ID = '290942676';

// Maximum amount of time to poll for Heap ID
const MAX_POLL_TIME_MS = 1000 * 7; // 7 Seconds

export function initializeHeap() {
  if (!isAnalyticsEnabled() || !window.heap) return;

  window.heap.load(Environment.current.isCloverProdEnvironment ? HEAP_PROD_ID : HEAP_TEST_ID, {
    disableTextCapture: true,
  });
}

export function addUserPropertiesToHeap(userProperties: {}) {
  if (!isAnalyticsEnabled() || !window.heap || !window.heap.addUserProperties) return;

  window.heap.addUserProperties(userProperties);
}

export function trackHeapEvent(eventName: string, metadata: object) {
  if (!isAnalyticsEnabled() || !window.heap) return;

  window.heap.track(eventName, metadata);
}

/**
 * We will be using the Heap ID as our global userID for analytics.
 * This function will wait for the heapID to be set (up to a certain time)
 *   then add it to a cookie and set up all the other analytics tools with this ID.
 */
export function setupAnalyticsId(): Promise<string> {
  if (!isAnalyticsEnabled()) return Promise.reject('No analytics enabled');

  if (getAnalyticsId()) {
    return Promise.resolve(getAnalyticsId());
  }

  if (!window.heap) {
    return Promise.reject('Heap not loaded onto window');
  }

  return new Promise((resolve, reject) => {
    let heapId;
    const maxEndTime = new Date().getTime() + MAX_POLL_TIME_MS;
    const intervalId = setInterval(() => {
      if (window.heap.userId) {
        heapId = window.heap.userId;
        clearInterval(intervalId);
        return resolve(heapId);
      }
      if (maxEndTime <= new Date().getTime()) {
        clearInterval(intervalId);
        return reject(`No HeapID found in ${MAX_POLL_TIME_MS / 1000} seconds`);
      }
    }, 500);
  });
}

export function getAnalyticsId(): string {
  const idCookie = readCookie(ANALYTICS_COOKIE_NAME);

  return idCookie || (window.heap && window.heap.userId);
}

export async function updateAnalyticsId(email: string | undefined) {
  if (!email) {
    return;
  }
  const growthApiClient = Environment.current.growthApiClient;

  return growthApiClient
    .post('/v1/inquiries', { email })
    .then(response => {
      if (!response.data || !response.data.id) {
        return;
      }

      identifyUser(response.data.id);
      updateServicesWithAnalyticsId(response.data.id);
    })
    .catch(err => {
      console.error(err);
    });
}

function identifyUser(heapId: string) {
  if (!window.heap) return;

  window.heap.identify(heapId);
}
